import { Inbox } from "lucide-react";
import type { SurfaceProgress, SurfaceTab } from "../api/types";
import { TAB_EMPTY_STATES, TAB_ICONS } from "../utils/tabHints";

type Props = {
  tab: SurfaceTab;
  progress?: SurfaceProgress;
  /** When feed filters are active but returned no rows. */
  filtersActive?: boolean;
};

/** Empty grid with a clear next step for the active tab. */
export default function PromoteSurfaceEmptyState({ tab, progress, filtersActive }: Props) {
  if (filtersActive) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Inbox className="h-5 w-5" aria-hidden strokeWidth={1.75} />
        </span>
        <div className="max-w-sm space-y-1.5">
          <h2 className="text-base font-semibold tracking-tight text-foreground">
            No images match these filters
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Clear or adjust include/exclude tags to see more of this tab.
          </p>
        </div>
      </div>
    );
  }

  const quotaFull =
    tab === "review" &&
    progress &&
    progress.pending_quota_remaining === 0 &&
    (progress.awaiting_suggestion ?? 0) > 0;

  if (quotaFull) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Inbox className="h-5 w-5" aria-hidden strokeWidth={1.75} />
        </span>
        <div className="max-w-sm space-y-1.5">
          <h2 className="text-base font-semibold tracking-tight text-foreground">
            Waiting on space for new drafts
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            The pending quota is full while more images still await AI. Accept or Reject items in
            Review to free slots — Skip does not count against the pending limit. This is quota
            pressure, not a broken pipeline.
          </p>
        </div>
      </div>
    );
  }

  const empty = TAB_EMPTY_STATES[tab];
  const Icon = TAB_ICONS[tab];

  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Icon className="h-5 w-5" aria-hidden strokeWidth={1.75} />
      </span>
      <div className="max-w-sm space-y-1.5">
        <h2 className="text-base font-semibold tracking-tight text-foreground">{empty.title}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">{empty.body}</p>
      </div>
    </div>
  );
}
